import { StatusIndicator } from '#components/status-indicator.js';
import { FLOW_TONE_CLASSES, getFlowPosition, getFlowStep } from '#utils/revision-flow.js';
import { getRevisionStatusColor, getRevisionStatusLabel } from '#utils/revision-utils.js';

import type { BuildAutomationMode, RevisionStatus } from '@cadstrom/cadstrom-shared';
import type { ReactElement } from 'react';

export interface RevisionFlowStatusProps {
	status: RevisionStatus;
	/** Distinguishes a revision awaiting an upload from one already queued. */
	hasUploadedFiles: boolean;
	/** Distinguishes an analysis gate waiting on a person from one under automation. */
	automationMode?: BuildAutomationMode;
	/** Whether the organization is on the billing flow, which decides who clears the analysis gate. */
	showBillingFlow: boolean;
	/** Render the leading dot. Turn off where a timeline rail already carries it. */
	showDot?: boolean;
	/** Pulse the dot while work is executing. */
	pulseWhenRunning?: boolean;
	className?: string;
}

/**
 * A revision's position in the pipeline as one compact line: the step it sits
 * on, the precise status beside it, both in the tone the step carries.
 *
 * Falls back to the raw status label for `error`, `abandoned` and `unknown`,
 * which describe a stopped revision rather than a point in the flow.
 */
export function RevisionFlowStatus({
	status,
	hasUploadedFiles,
	automationMode,
	showBillingFlow,
	showDot = true,
	pulseWhenRunning = false,
	className,
}: RevisionFlowStatusProps): ReactElement {
	const position = getFlowPosition({ status, hasUploadedFiles, automationMode, showBillingFlow });
	const step = getFlowStep(position);

	if (step === null) {
		const colors = getRevisionStatusColor(status);
		return (
			<StatusIndicator
				label={getRevisionStatusLabel(status)}
				labelClassName={colors.text}
				dotClassName={showDot ? colors.dot : undefined}
				className={className}
			/>
		);
	}

	const tone = FLOW_TONE_CLASSES[position.tone];

	return (
		<StatusIndicator
			label={step.name}
			labelClassName='text-gray-a12'
			suffix={position.suffix}
			suffixClassName={tone.text}
			dotClassName={showDot ? tone.fill : undefined}
			pulse={pulseWhenRunning && position.isRunning}
			className={className}
		/>
	);
}
